import React, { useState, useEffect, useCallback } from 'react';
import FormElemWrapper from './FormElemWrapper';
import useFieldValues from 'hooks/useFormFieldsGetter';
import { FormattedFields } from 'hooks/useFormFieldsSharedLogic';
import StateDrivenSlotDropdown from '../../StateDrivenDropdown';
import { Option, OptionList } from 'model/Form';
import SelectedFilters from 'components/Functional/ArticleFilterBar/SelectedFilters';
import Checkbox from '../../Checkbox';
import classNames from 'classnames';
import styles from './MultiplePicklist.module.scss';

interface MultiplePickListProps {
  optionList: OptionList;
  name: string;
  fields: FormattedFields;
  required?: boolean;
  getLabel?: string;
  getTooltip?: string;
  getHintMessage?: string;
  onFocus?: () => void;
  onUpdateDataLayer?: () => void;
  onChange: (selectedValues: string[]) => void;
}

const MultiplePickList: React.FC<MultiplePickListProps> = ({
  optionList,
  name,
  fields,
  onFocus,
  onUpdateDataLayer,
  onChange,
  required = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [appliedOptions, setAppliedOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const { label, tooltip, hintMessage } = useFieldValues(fields);

  useEffect(() => {
    setSelectedOptions(appliedOptions);
  }, [appliedOptions, isDropdownOpen]);
  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const selectOption = useCallback(
    (option: Option) => {
      if (selectedOptions.includes(option))
        setSelectedOptions((options) => options.filter((o) => o !== option));
      else setSelectedOptions((options) => [...options, option]);
    },
    [selectedOptions]
  );
  const applyOptions = useCallback(
    (options: Option[]) => {
      setAppliedOptions(options);
      setIsError(false);
      onChange(options.map((option) => option.value));
      onUpdateDataLayer?.();
      if (required && options.length === 0) {
        setIsError(true);
      } else {
        setIsError(false);
      }
    },
    [onChange, onUpdateDataLayer, required]
  );

  const deleteOptionAndApply = useCallback(
    (option: Option) => {
      const newOptions = appliedOptions.filter((o) => o !== option);
      applyOptions(newOptions);
      if (required && newOptions.length === 0) {
        setIsError(true);
      }
    },
    [appliedOptions, applyOptions, required]
  );

  const applySelectedOptions = () => {
    applyOptions(selectedOptions);
    setIsDropdownOpen(false);
  };

  const clearAndApply = useCallback(() => {
    setSelectedOptions([]);
    applyOptions([]);
    if (required) {
      setIsError(true);
    }
  }, [applyOptions, required]);

  return (
    <section onKeyDown={(e) => e.key === 'Escape' && toggleDropdown()}>
      <FormElemWrapper
        isError={isError}
        wrapperTag="div"
        className={classNames(styles['form-element--dropdown'], {
          [styles['form-element--error']]: isError,
        })}
        slots={{
          fieldLabel: label,
          hint: hintMessage && <div>{hintMessage}</div>,
          tooltip: tooltip,
          error: isError && <div>{fields.errorMessages.valueMissing}</div>,
        }}
      >
        <StateDrivenSlotDropdown
          isOpen={isDropdownOpen}
          className={styles.dropdown}
          contentClassName={styles['dropdown__content']}
          fullWidth={true}
          buttonActionType="button"
          showActionButtons={true}
          onOutsideClick={toggleDropdown}
          onFocus={onFocus}
          onLostFocus={() => setIsDropdownOpen(false)}
          onCancelEvent={clearAndApply}
          onConfirmEvent={applySelectedOptions}
          triggerAreaSlot={
            <button
              type="button"
              aria-expanded={isDropdownOpen}
              aria-controls="trigger-area"
              className={classNames(
                'd-flex align-items-center background-to-change btn btn-naked w-100',
                styles['pick-list-selector']
              )}
              onClick={() => setIsDropdownOpen(true)}
            >
              <span className="field-text">{name}</span>
              <span aria-hidden="true" tabIndex={-1} className="ms-auto background-to-change">
                <em className="d-flex bi-chevron-down" />
              </span>
            </button>
          }
        >
          {optionList.elements.map((option) => (
            <Checkbox
              className={styles['checkbox-wrapper']}
              boxClassName={styles.checkbox}
              key={option.value}
              tabIndex={isDropdownOpen ? 0 : -1}
              aria-hidden={isDropdownOpen ? 'false' : 'true'}
              selected={selectedOptions.includes(option)}
              label={option.displayName}
              onChange={() => selectOption(option)}
            />
          ))}
        </StateDrivenSlotDropdown>
      </FormElemWrapper>

      {appliedOptions.length > 0 && (
        <SelectedFilters
          appliedFilterOptions={appliedOptions}
          onClearFilters={clearAndApply}
          onDeleteOption={deleteOptionAndApply}
          className="mt-4"
        />
      )}
    </section>
  );
};

export default MultiplePickList;
